import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plane, AlertTriangle, ChevronDown, FileWarning, FileCheck2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DecisionEditor } from "./DecisionEditor";
import { DECISION_TEMPLATES, fillTemplate } from "../lib/decisionTemplates";

export const TRAVEL_STYLE = {
  returned: ["خرج ثم عاد", "bg-emerald-50 border-emerald-200 text-emerald-800", "bg-emerald-100 text-emerald-700"],
  traveling: ["مازال مسافر", "bg-orange-50 border-orange-200 text-orange-800", "bg-orange-100 text-orange-700"],
  not_returned: ["خرج ولم يعد", "bg-rose-50 border-rose-200 text-rose-800", "bg-rose-100 text-rose-700"],
  absconded: ["هروب", "bg-violet-50 border-violet-200 text-violet-800", "bg-violet-100 text-violet-700"],
};
const ORDER = ["traveling", "not_returned", "absconded", "returned"];

export const TravelTag = ({ status }) => {
  const t = TRAVEL_STYLE[status];
  return t ? <span className={`mr-1 rounded px-1 text-[10px] font-semibold ${t[2]}`} data-testid="travel-tag">{t[0]}</span> : null;
};

const ReportBtn = ({ x, onReport, onOpenReport }) => x.report_id ? (
  <button type="button" onClick={() => onOpenReport(x)} className="flex items-center gap-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-800 hover:bg-emerald-200" data-testid={`travel-report-link-${x.employee_id}`}>
    <FileCheck2 className="h-3.5 w-3.5" /> تم إصدار بلاغ {x.report_number}
  </button>
) : (
  <button type="button" onClick={() => onReport(x)} className="flex items-center gap-1 rounded bg-violet-600 px-1.5 py-0.5 text-[11px] font-semibold text-white hover:bg-violet-700" data-testid={`travel-report-btn-${x.employee_id}`}>
    <FileWarning className="h-3.5 w-3.5" /> إنشاء بلاغ هروب
  </button>
);

const Row = ({ x, onSet, onReport, onOpenReport }) => (
  <div className="flex flex-wrap items-center gap-2 border-t border-white/70 py-1.5 text-xs" data-testid={`travel-row-${x.employee_id}`}>
    <span className="min-w-[120px] flex-1 font-semibold">{x.name}</span>
    <span className="tabular-nums">خروج {x.exit_date}</span>
    <span className="tabular-nums">دخول {x.return_date || "—"}</span>
    {x.visa_expiry && <span className="tabular-nums">آخر موعد للعودة {x.visa_expiry}</span>}
    <span className="rounded bg-white/70 px-1.5 font-semibold" data-testid={`travel-duration-${x.employee_id}`}>
      الإجازة المعتمدة: {x.leave_days ? `${x.leave_days} يوم` : "—"} · الفعلي خارج المملكة: {x.abroad_days} يوم{x.duration_days ? ` · مدة التأشيرة ${x.duration_days}` : ""}</span>
    {x.abroad ? <span className="rounded-full bg-sky-600 px-2 py-0.5 font-bold text-white" data-testid={`travel-abroad-${x.employee_id}`}>خارج المملكة منذ {x.abroad_days} يوم</span>
      : x.return_date && <span className="rounded-full bg-emerald-600 px-2 py-0.5 font-bold text-white" data-testid={`travel-back-${x.employee_id}`}>عاد بتاريخ {x.return_date}</span>}
    {x.over_days > 0 && <span className="rounded-full bg-red-600 px-2 py-0.5 font-bold text-white" data-testid={`travel-over-${x.employee_id}`}>تجاوز إجازته بـ {x.over_days} يوم</span>}
    <select value={x.manual ? x.status : ""} onChange={(e) => onSet(x, e.target.value)} className="h-7 rounded border border-slate-200 bg-white px-1 text-[11px]" data-testid={`travel-set-${x.employee_id}`}>
      <option value="">تلقائي</option>
      {Object.entries(TRAVEL_STYLE).map(([k, v]) => <option key={k} value={k}>{v[0]}</option>)}
    </select>
    {x.status === "absconded" && <ReportBtn x={x} onReport={onReport} onOpenReport={onOpenReport} />}
  </div>
);

export const TravelStatusPanel = ({ refreshKey }) => {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const [openG, setOpenG] = useState({});
  const [emps, setEmps] = useState(null);
  const [dec, setDec] = useState(null);
  const { company } = useAuth();
  const load = useCallback(() => api.leavesTravel().then(setD).catch(() => {}), []);
  useEffect(() => { load(); }, [load, refreshKey]);
  const onSet = async (x, status) => {
    try { await api.setLeaveTravel(x.leave_id, status); toast.success("تم تحديث الحالة"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التحديث"); }
  };
  const getEmps = async () => {
    if (emps) return emps;
    const r = await api.listEmployees();
    const l = Array.isArray(r) ? r : (r.employees || r.items || []);
    setEmps(l);
    return l;
  };
  const onReport = async (x) => {
    try {
      const emp = (await getEmps()).find((e) => e.id === x.employee_id);
      const tp = DECISION_TEMPLATES.find((t) => t.key === "abscond");
      const date = new Date().toISOString().slice(0, 10);
      const body_html = fillTemplate(tp.body, emp, { company: company?.name || "الشركة", date, exit_date: x.exit_date,
        visa_expiry: x.visa_expiry, absence_date: x.visa_expiry || x.exit_date });
      setDec({ type: tp.type, title: tp.title, date, employee_id: x.employee_id, body_html, require_signature: false, leave_id: x.leave_id });
    } catch { toast.error("تعذّر تجهيز البلاغ"); }
  };
  const onOpenReport = async (x) => {
    try {
      const [, r] = await Promise.all([getEmps(), api.listDecisions()]);
      const found = (r.items || []).find((i) => i.id === x.report_id);
      if (found) setDec(found); else toast.error("القرار غير موجود (ربما حُذف)");
    } catch { toast.error("تعذّر فتح البلاغ"); }
  };
  if (!d) return null;
  const tog = (k) => setOpenG((o) => ({ ...o, [k]: !o[k] }));
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3" data-testid="travel-panel">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full flex-wrap items-center gap-2 text-start" data-testid="travel-panel-toggle">
        <span className="flex items-center gap-1.5 font-heading text-sm font-bold text-slate-800"><Plane className="h-4 w-4 text-sky-600" /> حالة المسافرين</span>
        {ORDER.map((k) => <span key={k} className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${TRAVEL_STYLE[k][2]}`} data-testid={`travel-count-${k}`}>{TRAVEL_STYLE[k][0]} {(d[k] || []).length}</span>)}
        <ChevronDown className={`ms-auto h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="mt-2 space-y-2">
        {!d.imported && <p className="flex items-center gap-1.5 rounded-lg bg-amber-50 p-2 text-xs text-amber-800" data-testid="travel-not-imported"><AlertTriangle className="h-4 w-4" /> لم يُستورد ملف التأشيرات من مقيم بعد — اضغط «رفع التأشيرات (مقيم)» ثم «تطبيق» لتظهر آخر إجازة وموعد الاستحقاق.</p>}
        {ORDER.map((k) => (
          <div key={k} className={`rounded-xl border p-2 ${TRAVEL_STYLE[k][1]}`} data-testid={`travel-group-${k}`}>
            <button type="button" onClick={() => tog(k)} disabled={!(d[k] || []).length} className="flex w-full items-center justify-between text-xs font-bold" data-testid={`travel-group-toggle-${k}`}>
              {TRAVEL_STYLE[k][0]} ({(d[k] || []).length})
              {(d[k] || []).length > 0 && <ChevronDown className={`h-3.5 w-3.5 transition-transform ${openG[k] ? "rotate-180" : ""}`} />}
            </button>
            {openG[k] && (d[k] || []).slice(0, 100).map((x) => <Row key={x.employee_id} x={x} onSet={onSet} onReport={onReport} onOpenReport={onOpenReport} />)}
          </div>
        ))}
      </div>}
      {dec && <DecisionEditor key={dec.id || `new-${dec.employee_id}`} open onOpenChange={(v) => !v && setDec(null)} decision={dec} employees={emps} onSaved={load} />}
    </div>
  );
};
